import React, { useEffect, useState } from "react";

import SunnySpinner from "ui/SunnySpinner";
import WelcomeScreen from "./WelcomeScreen";
import { StudentBasicInformationForm } from "components/StudentOnboarding";
import { OnboardingPageContainer } from "components/Onboarding";

import {
  useOnboardingStudentQuery,
  OnboardingStudentQuery,
} from "./OnboardingStudent.generated";

import ErrorModal from "components/ErrorModal";

import { SurfaceType } from "utils/analytics/analyticsConstants";
import { trackPageTransition } from "utils/analytics";
import assertExhausted from "helpers/assertExhausted";

import Stepbar from "ui/Stepbar";
import styled from "styled-components/macro";
import GuestLayout from "components/GuestLayout";
import IconCircleButton from "ui/IconCircleButton/IconCircleButton";

enum Step {
  WelcomeScreen = 1,
  BasicInformation,
  ShortNameFilled,
  GoToAccount,
}

// min-height to stop jump w/ appearing back button in final step
const $StepbarContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 4rem 0;
  min-height: 3.75rem;
`;

function useExtractOnboardingData(data?: OnboardingStudentQuery) {
  if (!data) {
    return;
  }

  const teachers = data.studentViewer.teachers;
  if (teachers.length < 1) {
    // in the seed data, a student can be connected with multiple teachers
    throw new Error("Unexpected number of teachers: " + teachers.length);
  }
  const teacher = teachers[0];

  return {
    studentId: data.studentViewer.id,
    fullName: data.studentViewer.fullName,
    teacherName: teacher.name,
    schoolName: teacher.school?.name,
  };
}

export default function StudentOnboardingFlow() {
  const [currentStep, setCurrentStep] = useState<Step>(Step.WelcomeScreen);

  const [termsOfServiceChecked, setTermsOfServiceChecked] = useState(false);
  const { data, error, loading } = useOnboardingStudentQuery();

  useEffect(() => {
    trackPageTransition(
      "viewed_student_onboarding_page",
      SurfaceType.NAVIGATION,
    );
  }, []);

  const onboardingData = useExtractOnboardingData(data);

  useEffect(() => {
    if (currentStep === Step.GoToAccount && onboardingData) {
      window.location.assign("/");
    }
  }, [currentStep, onboardingData]);

  if (loading) {
    return <SunnySpinner fullPage={true} />;
  }

  if (error) {
    return <ErrorModal analyticsSurface={SurfaceType.NAVIGATION} show={true} />;
  }

  if (!onboardingData) {
    throw new Error("Unable to fetch data: " + data);
  }

  switch (currentStep) {
    case Step.WelcomeScreen: {
      const children = (
        <>
          <$StepbarContainer>
            <Stepbar currentStep={currentStep} totalSteps={2} />
          </$StepbarContainer>
          <OnboardingPageContainer>
            <WelcomeScreen
              {...onboardingData}
              onNext={() => {
                setCurrentStep(Step.BasicInformation);
              }}
              onTermsOfServiceChange={setTermsOfServiceChecked}
              initialTermsOfServiceChecked={termsOfServiceChecked}
            />
          </OnboardingPageContainer>
        </>
      );
      return <GuestLayout userType="student">{children}</GuestLayout>;
    }
    case Step.BasicInformation:
    case Step.ShortNameFilled: {
      const children = (
        <>
          <$StepbarContainer>
            <Stepbar currentStep={currentStep} totalSteps={2} />
          </$StepbarContainer>
          <OnboardingPageContainer>
            <StudentBasicInformationForm
              {...onboardingData}
              onShortNameChange={(displayName) => {
                displayName.length > 0
                  ? setCurrentStep(Step.ShortNameFilled)
                  : setCurrentStep(Step.BasicInformation);
              }}
              onFinish={() => {
                setCurrentStep(Step.GoToAccount);
              }}
            />
          </OnboardingPageContainer>
        </>
      );
      return (
        <GuestLayout
          userType="student"
          backButton={
            <IconCircleButton
              icon="back"
              ariaLabel="Go back"
              analyticsName="clicked_back_on_basic_information"
              onClick={() => {
                setCurrentStep(Step.WelcomeScreen);
              }}
            />
          }
        >
          {children}
        </GuestLayout>
      );
    }
    case Step.GoToAccount:
      // Show spinner until redirect occurs
      return <SunnySpinner fullPage={true} />;
    default:
      assertExhausted(currentStep);
  }
}
